import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown } from 'react-bootstrap';
import DropdownContext from '@restart/ui/DropdownContext';

const DEFAULT_OFFSET = [0, 4];

/**
 * Dropdown.Menu that anchors to its toggle with plain getBoundingClientRect math.
 *
 * Popper’s fixed strategy paints at 0,0 for a frame inside overflow/flex layouts —
 * that flash is what users keep seeing. This skips Popper: measure the toggle,
 * portal to document.body, and stay invisible until coords exist (useLayoutEffect
 * runs before paint, so there is no top-left flicker).
 *
 * Use instead of bare Dropdown.Menu in order entry, trade card, sidebars, etc.
 * Spacing comes from `offset`, not CSS margin on the menu.
 */
export default function FixedPositionDropdownMenu({
  sameWidth = true,
  openUp = false,
  maxHeightAbove = false,
  offset = DEFAULT_OFFSET,
  padding = 12,
  style,
  children,
  ...rest
}) {
  const ctx = useContext(DropdownContext);
  const show = Boolean(ctx?.show);
  const toggleElement = ctx?.toggleElement;
  const menuRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!show || !toggleElement) {
      setCoords(null);
      return undefined;
    }

    const place = () => {
      const toggle = toggleElement.getBoundingClientRect();
      const menu = menuRef.current;
      const menuHeight = menu?.offsetHeight || 0;
      const menuWidth = sameWidth ? toggle.width : (menu?.offsetWidth || toggle.width);
      const skidding = offset[0] || 0;
      const gap = offset[1] ?? 4;

      const spaceBelow = window.innerHeight - toggle.bottom - padding;
      const spaceAbove = toggle.top - padding;
      const placeAbove = openUp
        || (menuHeight > 0 && spaceBelow < menuHeight + gap && spaceAbove > spaceBelow);

      let top = placeAbove ? toggle.top - gap - menuHeight : toggle.bottom + gap;
      let left = toggle.left + skidding;

      left = Math.min(Math.max(padding, left), Math.max(padding, window.innerWidth - menuWidth - padding));
      top = Math.min(Math.max(padding, top), Math.max(padding, window.innerHeight - Math.max(menuHeight, 1) - padding));

      const next = {
        top,
        left,
        width: sameWidth ? toggle.width : undefined,
        maxHeight: placeAbove && maxHeightAbove
          ? Math.max(120, Math.floor(toggle.top - padding - gap))
          : undefined,
      };

      setCoords((prev) => (
        prev
        && prev.top === next.top
        && prev.left === next.left
        && prev.width === next.width
        && prev.maxHeight === next.maxHeight
          ? prev
          : next
      ));
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [show, toggleElement, sameWidth, openUp, maxHeightAbove, offset, padding]);

  // align object → react-bootstrap disables Popper (we position ourselves).
  const menu = (
    <Dropdown.Menu
      {...rest}
      ref={menuRef}
      align={{ sm: 'start' }}
      style={{
        ...style,
        position: 'fixed',
        top: coords ? coords.top : 0,
        left: coords ? coords.left : 0,
        width: coords?.width,
        maxHeight: coords?.maxHeight ?? style?.maxHeight,
        margin: 0,
        // Hidden until measured — never paint at the viewport origin.
        visibility: coords ? 'visible' : 'hidden',
        pointerEvents: coords ? 'auto' : 'none',
        zIndex: style?.zIndex ?? 10050,
      }}
    >
      {children}
    </Dropdown.Menu>
  );

  if (typeof document === 'undefined') return menu;
  return createPortal(menu, document.body);
}
